import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const TermsPage = () => {
  const { t } = useTranslation();
  
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          Terms of Service
        </PageTitle>
        <PageDate
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          transition={{ delay: 0.1 }}
        >
          {t('legal.lastUpdated')}: April 18, 2025
        </PageDate>
      </PageHeader>

      <ContentContainer
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        transition={{ delay: 0.2 }}
      >
        <Section>
          <SectionTitle>1. Acceptance of Terms</SectionTitle>
          <SectionContent>
            By accessing or using the Xenya application, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this application.
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>2. Use License</SectionTitle>
          <SectionContent>
            Permission is granted to temporarily download one copy of the application for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
            <List>
              <ListItem>Modify or copy the materials</ListItem>
              <ListItem>Use the materials for any commercial purpose</ListItem>
              <ListItem>Attempt to decompile or reverse engineer any software contained in the application</ListItem>
              <ListItem>Remove any copyright or other proprietary notations from the materials</ListItem>
              <ListItem>Transfer the materials to another person or "mirror" the materials on any other server</ListItem>
            </List>
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>3. Disclaimer</SectionTitle>
          <SectionContent>
            The materials on Xenya's application are provided on an 'as is' basis. Xenya makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>4. Limitations</SectionTitle>
          <SectionContent>
            In no event shall Xenya or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on Xenya's application, even if Xenya or a Xenya authorized representative has been notified orally or in writing of the possibility of such damage.
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>5. Revisions and Errata</SectionTitle>
          <SectionContent>
            The materials appearing on Xenya's application could include technical, typographical, or photographic errors. Xenya does not warrant that any of the materials on its application are accurate, complete or current. Xenya may make changes to the materials contained on its application at any time without notice.
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>6. Links</SectionTitle>
          <SectionContent>
            Xenya has not reviewed all of the sites linked to its application and is not responsible for the contents of any such linked site. The inclusion of any link does not imply endorsement by Xenya of the site. Use of any such linked website is at the user's own risk.
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>7. Modifications to Terms of Service</SectionTitle>
          <SectionContent>
            Xenya may revise these terms of service for its application at any time without notice. By using this application you are agreeing to be bound by the then current version of these terms of service.
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>8. Governing Law</SectionTitle>
          <SectionContent>
            These terms and conditions are governed by and construed in accordance with the laws of France and you irrevocably submit to the exclusive jurisdiction of the courts in that location.
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>9. User Accounts and Security</SectionTitle>
          <SectionContent>
            When you create an account with us, you must provide accurate and complete information. You are solely responsible for the activity that occurs on your account, and you must keep your account password secure. You must notify Xenya immediately of any breach of security or unauthorized use of your account.
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>10. Content</SectionTitle>
          <SectionContent>
            Our application allows you to upload, share, and store content such as photos and text. You retain all rights to your content, but you grant Xenya a non-exclusive, royalty-free, transferable, sub-licensable, worldwide license to use, store, display, reproduce, modify, and distribute your content solely for the purposes of operating, developing, and improving our services.
          </SectionContent>
        </Section>

        <ContactSection>
          <SectionTitle>Contact Information</SectionTitle>
          <SectionContent>
            If you have any questions about these Terms of Service, please contact us at:
            <ContactInfo>
              <div>Xenya</div>
              <div>Email: legal@xenya-face.app</div>
              <div>Address: 123 Beauty Lane, Paris, France</div>
            </ContactInfo>
          </SectionContent>
        </ContactSection>
      </ContentContainer>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  padding-top: 80px;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: ${({ theme }) => theme.colors.gradientPrimary};
`;

const PageTitle = styled(motion.h1)`
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-bottom: 10px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes['3xl']};
  }
`;

const PageDate = styled(motion.p)`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.gray};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

const ContentContainer = styled(motion.div)`
  max-width: 900px;
  margin: 0 auto;
  padding: 60px 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  backdrop-filter: blur(10px);
  margin-top: -30px;
  margin-bottom: 60px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const Section = styled.div`
  margin-bottom: 40px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin-bottom: 15px;
  color: ${({ theme }) => theme.colors.accent};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }
`;

const SectionContent = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.md};
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.light};
  opacity: 0.9;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

const List = styled.ul`
  padding-left: 20px;
  margin: 15px 0;
`;

const ListItem = styled.li`
  margin-bottom: 8px;
  position: relative;
  padding-left: 5px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:before {
    content: '•';
    position: absolute;
    left: -15px;
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const ContactSection = styled(Section)`
  margin-top: 60px;
  padding-top: 30px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const ContactInfo = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 20px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-top: 15px;
  line-height: 1.8;
`;

export default TermsPage;